import * as Popover from "@radix-ui/react-popover";
import { useContext } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { cx } from "./klasy";
import type { KomponentIkony } from "./ikony";
import { LiniaKarty, type LiniaProduktu } from "./Okno";
import "../styles/wspolne.css";

export interface PozycjaMenu {
  id: string;
  etykieta: string;
  /** Krótki opis pod etykietą (co akcja zrobi albo dlaczego jest wyłączona) */
  opis?: string;
  ikona?: KomponentIkony;
  /** Akcja przycisku… */
  onWybierz?: () => void;
  /** …albo przejście do ekranu */
  do?: string;
  niebezpieczna?: boolean;
  wylaczona?: boolean;
}

export interface MenuProps {
  /** Element otwierający menu - zwykle PrzyciskIkona z IkonaKropki */
  wyzwalacz: ReactNode;
  pozycje: ReadonlyArray<PozycjaMenu>;
  /** Opcjonalny nagłówek (np. nazwa i e-mail konta) */
  naglowek?: ReactNode;
  wyrownanie?: "start" | "center" | "end";
  otwarte?: boolean;
  onZmianaOtwarcia?: (otwarte: boolean) => void;
  linia?: LiniaProduktu;
}

/** Menu akcji na @radix-ui/react-popover. Wybranie pozycji zamyka menu. */
export function Menu({ wyzwalacz, pozycje, naglowek, wyrownanie = "end", otwarte, onZmianaOtwarcia, linia }: MenuProps) {
  const liniaKarty = useContext(LiniaKarty);
  return (
    <Popover.Root open={otwarte} onOpenChange={onZmianaOtwarcia}>
      <Popover.Trigger asChild>{wyzwalacz}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="iot-menu iot-portal" align={wyrownanie} sideOffset={6} collisionPadding={12} data-iot-linia={linia ?? liniaKarty}>
          {naglowek && <div className="iot-menu__naglowek">{naglowek}</div>}
          {pozycje.map(({ id, etykieta, opis, ikona: Ikona, onWybierz, do: cel, niebezpieczna, wylaczona }) => {
            const klasa = cx("iot-menu__pozycja", niebezpieczna && "iot-menu__pozycja--niebezpieczna");
            const tekst = opis ? <span className="iot-menu__tekst">{etykieta}<small className="iot-menu__opis">{opis}</small></span> : etykieta;
            return (
              <Popover.Close asChild key={id}>
                {cel ? (
                  <Link to={cel} className={klasa}>{Ikona && <Ikona />}{tekst}</Link>
                ) : (
                  <button type="button" className={klasa} onClick={onWybierz} disabled={wylaczona}>{Ikona && <Ikona />}{tekst}</button>
                )}
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export interface DymekProps {
  wyzwalacz: ReactNode;
  children: ReactNode;
  wyrownanie?: "start" | "center" | "end";
  etykieta: string;
  linia?: LiniaProduktu;
}

/** Dymek z dowolną treścią (np. szybka zmiana celu, filtr) na @radix-ui/react-popover. */
export function Dymek({ wyzwalacz, children, wyrownanie = "center", etykieta, linia }: DymekProps) {
  const liniaKarty = useContext(LiniaKarty);
  return (
    <Popover.Root>
      <Popover.Trigger asChild>{wyzwalacz}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="iot-menu iot-portal iot-menu--dymek" align={wyrownanie} sideOffset={6} collisionPadding={12} aria-label={etykieta} data-iot-linia={linia ?? liniaKarty}>
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
